import { Box, Paper, Typography } from "@mui/material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { REPORT_COLORS } from "../../../utils/distinctColors";

export type DealsByMonthPoint = {
  key: string;
  label: string;
  count: number;
};

type ChartTooltipProps = {
  active?: boolean;
  payload?: Array<{ value?: number }>;
  label?: string;
};

function ChartTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;

  const count = payload[0]?.value;
  if (count == null) return null;

  return (
    <Paper
      elevation={3}
      sx={{
        position: "relative",
        zIndex: 20,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "#fff",
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#182737" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#182737", mt: 0.25 }}>
        {count} deals
      </Typography>
    </Paper>
  );
}

export function DealsByMonthLineChart(props: {
  points: DealsByMonthPoint[];
  title?: string;
}) {
  const { points, title } = props;

  if (!points || points.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography sx={{ fontSize: "14px", color: "#182737" }}>
          No deals found to chart.
        </Typography>
      </Box>
    );
  }

  const chartData = points.map((point) => ({
    ...point,
    month: point.label,
  }));

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        padding: 2,
      }}
    >
      <Typography className="text-general-section-title" sx={{ mb: 1 }}>
        {title ?? "Deals Logged by Month"}
      </Typography>

      <Box sx={{ width: "100%", height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 12, right: 18, left: 4, bottom: 8 }}
          >
            <CartesianGrid
              stroke="#E6EEF3"
              strokeDasharray="0"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fill: "#182737", fontSize: 11 }}
              axisLine={{ stroke: "#B7C6CF" }}
              tickLine={{ stroke: "#B7C6CF" }}
              interval="preserveStartEnd"
              minTickGap={32}
            />
            <YAxis
              tick={{ fill: "#182737", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              width={36}
            />
            <Tooltip
              content={<ChartTooltip />}
              wrapperStyle={{ zIndex: 30, pointerEvents: "none" }}
              cursor={{ stroke: "#B7C6CF", strokeWidth: 1 }}
            />
            <Line
              type="monotone"
              dataKey="count"
              stroke={REPORT_COLORS.green}
              strokeWidth={3}
              dot={{
                r: 4,
                fill: REPORT_COLORS.green,
                stroke: "#ffffff",
                strokeWidth: 2,
              }}
              activeDot={{
                r: 5,
                fill: REPORT_COLORS.green,
                stroke: "#ffffff",
                strokeWidth: 2,
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
